<template>
  <div>
    <div class="boss-scout-send-container">
      <form-label>
        <template #label>
          テンプレート
          <span
            v-tooltip="{ content: 'メッセージテンプレートの作成・編集は、「各種設定」メニューで行えます。'}"
            class="pega-circle"
          >?</span>
        </template>
        <template #body>
          <select
            :value="selectedTemplateId"
            class="boss-input-select"
            @change="setSelectedTemplateMessage($event.target.value, selectedTemplateId)"
          >
            <option
              :value="null"
              data-test="template-option-none"
            >
              指定なし
            </option>
            <option
              v-for="(template, i) in templates"
              :key="i"
              :data-test="'template-option-' + i"
              :value="template.id"
            >
              {{ template.name }}
            </option>
          </select>
        </template>
      </form-label>
      <scout-mail-form
        v-model="innerMessage"
        :check-template="true"
        @change-invalid="changeInvalid"
      />
      <form-label label="送信日時">
        <template #body>
          <div>
            <label
              v-if="!reservationId"
              class="pega-input display-inline-block"
            >
              <input
                :checked="isImmediately === true"
                :value="true"
                class="boss-sent-radio"
                type="radio"
                data-test="immediately"
                @change="onImmediately(true)"
              >
              <span class="boss-sent-radio-text">即時送信</span>
            </label>
            <div v-if="isScoutedForTransition">
              <label
                class="display-inline boss-annotation-text"
                data-test="scout-annotation-text"
              >※スカウト配信は1名につき1日1通までです。本日配信済みの会員は送信時に除外されます。</label>
            </div>
          </div>
          <div class="boss-sent-date">
            <label class="pega-input display-inline-block">
              <input
                :checked="isImmediately === false"
                :value="false"
                class="boss-sent-radio"
                type="radio"
                data-test="send-reservation"
                @change="onImmediately(false)"
              >
              <span class="boss-sent-radio-text margin-right-1">送信日時の指定</span>
              <input
                :value="reservationName"
                class="boss-send-subject width-25 display-inline ie-vertical-text-bottom"
                placeholder="スカウト送信予約名"
                type="text"
                data-test="message-reservation-name"
                @focusin="onImmediately(false)"
                @input="$emit('update:reservationName', $event.target.value)"
              >
              <date-picker
                :value="sendDate"
                type="date"
                :disabled-date="notTodayBetweenScoutEndDate"
                class="ie-vertical-text-bottom"
                input-class="pega-picker"
                :style="{ width: 'auto' }"
                placeholder="日付を選択"
                :editable="false"
                value-type="YYYY-MM-DD"
                @change="onUpdateDate"
              />
              <date-picker
                class="ie-vertical-text-bottom"
                :value="sendTime"
                :minute-step="30"
                :disabled-time="disabledTime"
                :default-value="defaultTime"
                type="time"
                popup-class="pega-custom-time-header"
                placeholder="時間を選択"
                format="HH:mm"
                input-class="pega-picker"
                :style="{ width: 'auto' }"
                :editable="false"
                value-type="HH:mm"
                @change="onUpdateTime"
              />
            </label>
            <div>
              <label
                class="display-inline boss-annotation-text"
                data-test="remind-sent"
              >※予約できる時間は7:00～21:00までです。</label>
            </div>
          </div>
        </template>
      </form-label>
    </div>

    <accept-items
      v-if="innerMessage.specialFavorIds.length > 0"
      :accepted.sync="accepted"
      :notice-label="'以下のスカウト特典が設定されています。応募があった際は必ずご対応ください。'"
    >
      <template #body>
        <div
          v-for="checkedSpecialFavor in checkedSpecialFavors"
          :key="checkedSpecialFavor.id"
          class="grid-x grid-margin-x"
        >
          <li :data-test="'special-favor-warning-label-' + checkedSpecialFavor.id">
            {{ checkedSpecialFavor.name }}：{{ checkedSpecialFavor.description }}
          </li>
        </div>
      </template>
    </accept-items>
    <form-button-area>
      <form-back-button
        data-test="back-button"
        @back="$router.back()"
      />
      <form-confirm-button
        :is-disabled="isConfirmButtonDisabled"
        submit-label="送信内容を確認する"
        data-test="confirm-button"
        @submit-event="$emit('show-detail')"
      />
    </form-button-area>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import DatePicker from 'vue2-datepicker'
import datePickerFunction from '@/lib/datePickerFunction.js'
import dayjs from 'dayjs'
import ScoutMailForm from '@/components/molecules/ScoutMailForm'
import FormLabel from '@/components/atoms/FormLabel'
import FormButtonArea from '@/components/atoms/FormButtonArea'
import FormConfirmButton from '@/components/atoms/FormConfirmButton'
import FormBackButton from '@/components/atoms/FormBackButton'
import AcceptItems from '@/components/atoms/AcceptItems'

export default {
  components: {
    DatePicker,
    ScoutMailForm,
    FormLabel,
    FormButtonArea,
    FormConfirmButton,
    FormBackButton,
    AcceptItems
  },
  mixins: [datePickerFunction],
  props: {
    message: {
      type: Object,
      required: true
    },
    sendDate: {
      type: String,
      default: null
    },
    sendTime: {
      type: String,
      default: null
    },
    reservationName: {
      type: String,
      default: ''
    },
    isImmediately: {
      type: Boolean,
      required: true
    },
    checkedUserIds: {
      type: Array,
      required: true
    },
    scoutEndDate: {
      type: String,
      default: null
    },
    reservationId: {
      type: Number,
      default: null
    },
    isScoutedForTransition: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      templates: [],
      selectedTemplateId: null,
      accepted: false,
      isMessageInvalid: true
    }
  },
  computed: {
    innerMessage: {
      get () { return this.message },
      set (value) { this.$emit('update:message', value) }
    },
    isAccepted () {
      return this.innerMessage.specialFavorIds.length === 0 || (this.innerMessage.specialFavorIds.length > 0 && this.accepted)
    },
    isConfirmButtonDisabled () {
      return this.isMessageInvalid ||
        !this.isAccepted ||
        _.isEmpty(this.checkedUserIds) ||
        this.isReservationValidation || this.checkedUserIds.length > 2000
    },
    isReservationValidation () {
      return !this.isImmediately && (!this.sendDate ||
        !this.sendTime ||
        !this.reservationName)
    },
    checkedSpecialFavors () {
      return _.filter(this.specialFavors, specialFavor =>
        _.some(this.innerMessage.specialFavorIds, specialFavorId =>
          specialFavorId === specialFavor.id
        )
      )
    },
    disabledTime () {
      if (dayjs(this.sendDate).isSame(dayjs(), 'day')) {
        return this.isNowBeforeTime
      } else {
        return this.notNightReservationTime
      }
    },
    defaultTime () {
      if (dayjs(this.sendDate).isSame(dayjs(), 'day')) { // 当日
        const sendTime = this.todayLatestSendTime() // 現在時刻+30分以降で直近の便
        if (!this.notNightReservationTime(sendTime.toDate())) { // 送信可能時刻内
          return sendTime
        }
      }
      // それ以外の場合は08:00
      return this.defaultValueInDaytime
    },
    ...mapGetters({
      specialFavors: 'specialFavorsMaster',
      currentUser: 'currentUser'
    })
  },
  watch: {
    'message.specialFavorIds': {
      handler: 'clearAccepted'
    }
  },
  created () {
    this.storeMaster('specialFavors')
    this.fetchTemplates()
  },
  methods: {
    ...mapActions([
      'storeMaster'
    ]),
    fetchTemplates () {
      this.$http.secured.get('/company/scout/message/templates/_display').then(response => {
        this.$set(this, 'templates', response.data)
      })
    },
    fetchTemplate () {
      this.$http.secured.get(`/company/scout/message/templates/${this.selectedTemplateId}`).then(response => {
        const template = response.data
        this.$set(this, 'innerMessage', {
          body: template.body,
          subject: template.subject,
          specialFavorIds: template.specialFavorIds
        })
      })
    },
    setSelectedTemplateMessage (newId, oldId) {
      if (!newId) {
        // 「指定なし」が選択された場合は、selectedTemplateIdのみ更新
        this.$set(this, 'selectedTemplateId', null)
        return
      }

      if (!this.innerMessage.subject && !this.innerMessage.body && !this.innerMessage.specialFavorIds.length) {
        // 件名・本文・スカウト特典が未入力の場合にテンプレートが変更された場合は、テンプレートを反映
        this.$set(this, 'selectedTemplateId', newId)
        this.fetchTemplate()
        return
      }

      // 件名 または 本文 または スカウト特典が入力済みの場合にテンプレートが変更された場合は、確認ダイアログを出す
      this.$dialog.confirm('現在入力している件名・本文・スカウト特典が削除されます。テンプレートを適応してよろしいですか？')
        // はいを押下した時：テンプレートを反映
        .then(() => {
          this.$set(this, 'selectedTemplateId', newId)
          this.fetchTemplate()
        })
        // いいえを押下した時：選択前の値に戻す
        .catch(() => {
          // 一度空更新しないと、同一の値とみなされてselectに反映されないため、空更新してからoldIdを連携
          this.$set(this, 'selectedTemplateId', '')
          this.$set(this, 'selectedTemplateId', oldId)
        })
    },
    clearAccepted () {
      if (this.message.specialFavorIds.length === 0) {
        this.accepted = false
      }
    },
    onImmediately (val) {
      this.$emit('update:isImmediately', val)
    },
    onUpdateDate (val) {
      this.$emit('update:sendDate', val)
    },
    onUpdateTime (val) {
      this.$emit('update:sendTime', val)
    },
    notTodayBetweenScoutEndDate (date) {
      // 送信可能時刻以降の場合は当日を含めない
      const minDay = this.notAfterNightReservationTime(this.todayLatestSendTime().toDate())
        ? dayjs().add(1, 'day') : dayjs()
      return dayjs(date).isBefore(minDay, 'day') || date > dayjs(this.scoutEndDate)
    },
    isNowBeforeTime (date) {
      return dayjs(date).isBefore(dayjs().add(30, 'minutes'), 'minutes') ||
        this.notNightReservationTime(date)
    },
    todayLatestSendTime () {
      // 現在時刻+30分以降で直近の便の時刻を算出する
      // 便は毎時00分と30分（送信可能時間未考慮）
      const latestTime = dayjs().add(30, 'minutes')
      return (latestTime.minute() < 30)
        ? latestTime.startOf('hour').add(30, 'minutes')
        : latestTime.startOf('hour').add(1, 'hour')
    },
    changeInvalid (isInvalid) {
      this.$set(this, 'isMessageInvalid', isInvalid)
    }
  }
}
</script>
